import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { RefreshCw, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { RevenueRecords } from "@/components/admin/RevenueRecords";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleString(undefined, { month: "short", year: "2-digit", timeZone: "UTC" });
const NON_PAYING = [["trial", "On trial"], ["referral", "Invite rewards"], ["granted", "Admin grants"], ["event", "Free event"], ["admin", "Admins"], ["billing_paused", "Stripe trialing / billing paused"], ["free", "Free plan"]];
const card = "rounded-md border border-[#2D3442] bg-[#0F131A] p-6";

const Stat = ({ label, value, id }) => (
  <div className={card} data-testid={`revenue-stat-${id}`}>
    <div className="lf-label">{label}</div>
    <div className="mt-2 font-display text-3xl text-[#F3EBDD]" data-testid={`revenue-stat-${id}-value`}>{value}</div>
  </div>
);

const Table = ({ title, rows, cols, id }) => (
  <section className={card} data-testid={`revenue-${id}`}>
    <div className="lf-label">{title}</div>
    <table className="mt-4 w-full text-sm">
      <thead><tr className="text-left text-xs text-[#A8A090]">{cols.map(([, h]) => <th key={h} className="pb-2 font-normal">{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-t border-[#2D3442] text-[#F3EBDD]" data-testid={`revenue-${id}-row-${r.key}`}>
            {cols.map(([f]) => <td key={f} className="py-2">{f === "mrr" ? usd(r[f]) : r[f]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

const IncomeChart = ({ income }) => (
  <section className={card} data-testid="revenue-income-chart">
    <div className="lf-label">Income, last 12 months (net of refunds)</div>
    <div className="mt-4 h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={income.map((r) => ({ ...r, label: monthLabel(r.month) }))}>
          <CartesianGrid stroke="#2D3442" vertical={false} />
          <XAxis dataKey="label" stroke="#A8A090" fontSize={11} tickLine={false} />
          <YAxis stroke="#A8A090" fontSize={11} tickLine={false} tickFormatter={(v) => `$${v}`} />
          <Tooltip cursor={{ fill: "#F59E0B10" }} contentStyle={{ background: "#0B0D10", border: "1px solid #2D3442", color: "#F3EBDD" }}
            formatter={(v, n, p) => [`${usd(v)} · ${p.payload.payments} payments`, "Net"]} />
          <Bar dataKey="net" fill="#F59E0B" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </section>
);

const NonPaying = ({ counts }) => (
  <section className={card} data-testid="revenue-non-paying">
    <div className="lf-label">Non-paying accounts (excluded from income)</div>
    <div className="mt-4 flex flex-wrap gap-6">
      {NON_PAYING.map(([k, l]) => (
        <div key={k} data-testid={`revenue-non-paying-${k}`}><div className="text-2xl text-[#F3EBDD]">{counts[k] || 0}</div><div className="text-xs text-[#A8A090]">{l}</div></div>
      ))}
    </div>
  </section>
);

export const RevenuePanel = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, error, isLoading } = useQuery({ queryKey: ["admin-revenue"], queryFn: () => api.get("/admin/revenue").then((r) => r.data) });
  const refresh = async () => {
    setBusy(true);
    try { qc.setQueryData(["admin-revenue"], (await api.get("/admin/revenue", { params: { refresh: true } })).data); qc.invalidateQueries({ queryKey: ["admin-revenue-records"] }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (isLoading) return <p className="text-sm text-[#A8A090]" data-testid="revenue-loading">Loading revenue from Stripe…</p>;
  if (error) return <p className="text-sm text-red-400" data-testid="revenue-error">{errMsg(error)}</p>;
  return (
    <div className="space-y-6" data-testid="admin-revenue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#FBBF24]" /><span className="text-xs text-[#A8A090]" data-testid="revenue-updated">Live from Stripe · updated {new Date(data.generated_at).toLocaleTimeString()}</span></div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={busy} data-testid="revenue-refresh"><RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />Refresh</Button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat id="mrr" label="Monthly recurring" value={usd(data.mrr)} />
        <Stat id="arr" label="Annual run-rate" value={usd(data.arr)} />
        <Stat id="paying" label="Paying subscribers" value={data.paying} />
        <Stat id="income" label="Income, 12 months" value={usd(data.income_12m)} />
      </div>
      <IncomeChart income={data.income} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Table id="plans" title="By plan" rows={data.plans} cols={[["name", "Plan"], ["subscribers", "Subscribers"], ["monthly", "Monthly"], ["yearly", "Yearly"], ["mrr", "MRR"]]} />
        <Table id="features" title="By feature" rows={data.features} cols={[["name", "Feature"], ["subscribers", "Subscribers with access"], ["mrr", "À la carte MRR"]]} />
      </div>
      <p className="text-xs text-[#A8A090]">Yearly plans count as 1/12 of their price per month. Feature access includes Pro and Commercial bundles; à la carte MRR is income from that feature bought on its own.</p>
      <NonPaying counts={data.non_paying} />
      <RevenueRecords />
    </div>
  );
};
